import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CLINIC, CONDITIONS } from "@/data/clinic";
import { toast } from "sonner";

export default function AppointmentForm({ compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    condition: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", {
        ...form,
        email: form.email || undefined,
        condition: form.condition || undefined,
        preferred_date: form.preferred_date || undefined,
        message: form.message || undefined,
      });
      toast.success("Appointment request received. We will call you shortly.");
      setForm({ name: "", phone: "", email: "", condition: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="appointment-form"
      className={`bg-white border border-slate-100 rounded-3xl shadow-sm ${compact ? "p-6" : "p-7 lg:p-9"}`}
    >
      <div className="mb-5">
        <div className="text-xs uppercase tracking-[0.2em] text-green-700 font-medium">Book an appointment</div>
        <h3 className="font-serif text-2xl text-slate-900 mt-1">Schedule your consultation</h3>
        <p className="text-sm text-slate-500 mt-1">We respond within 30 minutes during clinic hours.</p>
      </div>
      <div className={`grid ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"} gap-3`}>
        <input
          type="text" required placeholder="Full Name *" value={form.name} onChange={update("name")}
          data-testid="appt-name"
          className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm"
        />
        <input
          type="tel" required placeholder="Phone Number *" value={form.phone} onChange={update("phone")}
          data-testid="appt-phone"
          className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm"
        />
        <input
          type="email" placeholder="Email (optional)" value={form.email} onChange={update("email")}
          data-testid="appt-email"
          className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm"
        />
        <input
          type="date" value={form.preferred_date} onChange={update("preferred_date")}
          data-testid="appt-date"
          className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm text-slate-700"
        />
        <select
          value={form.condition} onChange={update("condition")}
          data-testid="appt-condition"
          className={`px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm ${compact ? "" : "sm:col-span-2"}`}
        >
          <option value="">Select condition / concern</option>
          {CONDITIONS.map((c) => <option key={c.slug} value={c.title}>{c.title}</option>)}
          <option value="General Consultation">General Consultation</option>
        </select>
        <textarea
          placeholder="Brief message (symptoms, reports, urgency...)"
          value={form.message} onChange={update("message")} rows={compact ? 3 : 4}
          data-testid="appt-message"
          className={`px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 text-sm ${compact ? "" : "sm:col-span-2"}`}
        />
      </div>
      <button
        type="submit" disabled={loading}
        data-testid="appt-submit"
        className="mt-5 w-full bg-green-700 hover:bg-green-600 disabled:bg-green-700/60 text-white rounded-full px-6 py-3.5 font-medium text-sm transition-all shadow-sm hover:shadow-md"
      >
        {loading ? "Submitting..." : "Request Appointment"}
      </button>
      <div className="mt-4 flex flex-col sm:flex-row gap-3 text-xs text-slate-500">
        <a href={`tel:+91${CLINIC.phone}`} className="flex-1 text-center py-2 rounded-full border border-slate-200 hover:border-green-500 hover:text-green-700 transition" data-testid="appt-call-fallback">
          📞 Call {CLINIC.phoneDisplay}
        </a>
        <a
          href={`https://wa.me/${CLINIC.whatsapp}`}
          target="_blank" rel="noreferrer"
          className="flex-1 text-center py-2 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition"
          data-testid="appt-whatsapp-fallback"
        >
          💬 WhatsApp now
        </a>
      </div>
    </form>
  );
}
